import { priorDocumentReference } from '../shared/priorDocumentReference';
import { RelatedDocumentReviewPanel } from './RelatedDocumentReviewPanel';
import { LifecyclePriorDocPicker, type PriorDocument } from '../shared/LifecyclePriorDocPicker';
import {
  useCallback,
  useState,
  useId,
  useRef,
  type ChangeEvent as ReactChangeEvent,
  type CSSProperties,
  type ReactElement,
} from 'react';
import { v2, type Document } from '@/api/v2Client';
import { useSubmissionDraft } from '@/hooks/useSubmissionWorkspace';
import { parseApiErrorBody } from '@/api/v2Client';
import {
  LIFECYCLE_DESCRIPTIONS,
  LIFECYCLE_OPERATIONS,
  normalizeLifecycle,
  type LifecycleOperation,
} from './lifecycle';

/**
 * Wave E.3 — Inline lifecycle operation editor.
 *
 * Renders a labelled dropdown (new / replace / append / delete) in the
 * inspector header, above the tier-1 tabs. Saves via
 * `v2.documents.assignLifecycle` after selecting a prior document when required. An optional reason
 * textarea appears whenever the user stages an operation or target change; the
 * `reason` field is always OPTIONAL for lifecycle changes (required
 * reasons only apply to classifier overrides per Wave E.2).
 *
 * Role gating: when `canEdit` is false the select renders disabled
 * with an explanatory tooltip. The backend also gates on Documents.write,
 * so this is purely a UX-affordance check.
 */

export interface LifecycleOperationEditorProps {
  readonly documentId: string;
  readonly submissionId: string;
  readonly sectionCode?: string | null;
  readonly currentOperation: string | null | undefined;
  readonly rowVersion?: Document['RowVersion'];
  readonly sourceDocument?: Document;
  readonly canEdit: boolean;
  /** Called after a successful PATCH so the parent can refresh data. */
  readonly onSuccess: (newOperation: LifecycleOperation) => void;
  /** Called on PATCH error; the parent typically shows a toast. */
  readonly onError: (message: string) => void;
}

const wrapperStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
  padding: '8px 10px',
  marginBottom: 12,
  background: 'var(--surface2)',
  border: '1px solid var(--border)',
  borderRadius: 6,
};

const labelStyle: CSSProperties = {
  fontSize: 11,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color: 'var(--text-muted)',
  fontWeight: 700,
};

const selectStyle: CSSProperties = {
  fontSize: 13,
  padding: '4px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  background: 'var(--surface)',
  color: 'var(--text)',
  cursor: 'pointer',
};

const reasonRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 8,
  marginTop: 8,
  width: '100%',
};

const reasonInputStyle: CSSProperties = {
  flex: 1,
  minHeight: 36,
  padding: '6px 8px',
  border: '1px solid var(--border)',
  borderRadius: 4,
  fontSize: 13,
  fontFamily: 'inherit',
  resize: 'vertical',
};

const saveBtnStyle: CSSProperties = {
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
};

const cancelBtnStyle: CSSProperties = {
  background: 'transparent',
  color: 'var(--text-muted)',
  border: '1px solid var(--border)',
  borderRadius: 4,
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const disabledBtnStyle: CSSProperties = {
  ...saveBtnStyle,
  background: 'var(--surface2)',
  color: 'var(--text-muted)',
  cursor: 'not-allowed',
};

export function LifecycleOperationEditor({
  documentId,
  submissionId,
  sectionCode,
  currentOperation,
  rowVersion,
  sourceDocument,
  canEdit,
  onSuccess,
  onError,
}: LifecycleOperationEditorProps): ReactElement {
  const current = normalizeLifecycle(currentOperation);
  const [pending, setPending] = useState<LifecycleOperation | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [picking, setPicking] = useState(false);
  const [expectedRevision, setExpectedRevision] = useState<Document['RowVersion']>();
  const [selectedPrior, setSelectedPrior] = useState<PriorDocument | null>(null);
  const selectId = useId();
  const selectRef = useRef<HTMLSelectElement>(null);

  const showReasonRow = pending !== null && (pending !== current || selectedPrior !== null);
  const stale = pending !== null && JSON.stringify(expectedRevision) !== JSON.stringify(rowVersion);
  const chooseOperation = selectedPrior !== null && pending === 'new';
  const reviewOperation = pending ?? current;

  const handleSelectChange = (e: ReactChangeEvent<HTMLSelectElement>): void => {
    const v = e.target.value as LifecycleOperation;
    if (pending === null) setExpectedRevision(rowVersion);
    setPending(v === current && (v === 'new' || selectedPrior === null) ? null : v);
    if (v === current) {
      // Reset the reason if the user reverted to the current value.
      setReason('');
    }
    if (v === 'new') setSelectedPrior(null);
  };

  const handleCancel = useCallback((): void => {
    setPending(null);
    setReason('');
    setSelectedPrior(null);
    setPicking(false);
  }, []);

  // Register the staged lifecycle edit as workspace draft state, exactly like
  // the rename and navigation editors. Without this the always-available
  // document picker in the shared header could switch documents (a `key`
  // remount) and silently drop a pending operation + prior target + reason with
  // no "discard your unsaved change?" prompt (Kimi F6). `dirty` is true whenever
  // an operation or prior target is staged (a typed reason only exists while one
  // is), so the switch is guarded. `saving` marks the source busy so a context
  // switch cannot interrupt an in-flight save. `handleCancel` is the discard: it
  // clears ONLY this editor's pending state, never another editor's draft.
  const dirty = pending !== null || selectedPrior !== null;
  useSubmissionDraft(dirty, handleCancel, saving);

  const handleSave = async (): Promise<void> => {
    if (!canEdit || saving || stale || !pending || chooseOperation) return;
    const prior = selectedPrior;
    const savedPriorId = pending !== 'new' ? sourceDocument?.ReplacesDocumentId : null;
    if (pending !== 'new' && !prior && !savedPriorId) { setPicking(true); return; }
    setPicking(false);
    setSaving(true);
    try {
      await v2.documents.assignLifecycle(documentId, {
        lifecycleOperation: pending,
        replacesDocumentId: prior?.Id ?? savedPriorId ?? null,
        modifiedFile: prior ? priorDocumentReference(prior) : savedPriorId ? sourceDocument?.ModifiedFile ?? null : null,
        reason: reason.trim() === '' ? null : reason.trim(),
      }, expectedRevision);
      onSuccess(pending);
      setPending(null);
      setReason('');
      setSelectedPrior(null);
    } catch (err) {
      const body = parseApiErrorBody(err);
      const msg = body?.message ?? body?.error ?? (err instanceof Error ? err.message : String(err));
      onError(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="lifecycle-operation-editor" style={wrapperStyle}>
      {picking && pending && <LifecyclePriorDocPicker submissionId={submissionId} currentDocumentId={documentId} operation={pending}
        sectionCode={sectionCode ?? null} onSelect={prior => { setSelectedPrior(prior); setPicking(false); }}
        onCancel={() => { setPicking(false); if (pending === current && selectedPrior === null) setPending(null); }} />}
      <label htmlFor={selectId} style={labelStyle}>Lifecycle Op</label>
      <select
        id={selectId}
        ref={selectRef}
        data-testid="lifecycle-operation-select"
        value={pending ?? current}
        onChange={handleSelectChange}
        disabled={!canEdit || saving}
        title={
          !canEdit
            ? 'Lifecycle changes require Author or higher role.'
            : LIFECYCLE_DESCRIPTIONS[pending ?? current]
        }
        style={{
          ...selectStyle,
          ...(canEdit ? null : { cursor: 'not-allowed', opacity: 0.6 }),
        }}
      >
        {LIFECYCLE_OPERATIONS.map((op) => (
          <option key={op} value={op}>
            {op.toUpperCase()}
          </option>
        ))}
      </select>
      {(pending ?? current) !== 'new' && <button type="button" disabled={!canEdit || saving || stale} style={cancelBtnStyle}
        onClick={() => { if (pending === null) setExpectedRevision(rowVersion); setPending(pending ?? current); setPicking(true); }}>
        Change prior document
      </button>}
      {current !== 'new' && <div style={{ width: '100%', fontSize: 12 }}>
        Saved prior document: {sourceDocument?.ModifiedFile || sourceDocument?.ReplacesDocumentId || 'not recorded'}.
        {' '}This {current} operation acts on that earlier document.
      </div>}
      {selectedPrior && <div role="status" style={{ width: '100%', fontSize: 12 }}>
        Selected prior: {selectedPrior.FileName} · Seq {selectedPrior.SubSequenceNumber ?? 'unknown'}.
        {chooseOperation ? ' Choose an operation to apply to this document, then save.' : ' Review the operation and save to apply this target.'}
      </div>}
      {stale && <p role="alert">This document changed while you were reviewing. Cancel this edit and review the updated document before saving.</p>}

      {showReasonRow && (
        <div style={reasonRowStyle} data-testid="lifecycle-operation-pending">
          <textarea
            data-testid="lifecycle-operation-reason"
            aria-label="Optional reason for lifecycle change"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Optional reason (recorded in audit log)"
            disabled={saving}
            style={reasonInputStyle}
          />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button
              type="button"
              data-testid="lifecycle-operation-save"
              onClick={() => { void handleSave(); }}
              disabled={saving || stale || chooseOperation || !canEdit}
              style={saving || stale || chooseOperation || !canEdit ? disabledBtnStyle : saveBtnStyle}
            >
              {saving ? 'Saving…' : chooseOperation ? 'Choose an operation' : pending === current ? 'Save prior document' : `Change to ${pending?.toUpperCase()}`}
            </button>
            <button
              type="button"
              data-testid="lifecycle-operation-cancel"
              onClick={handleCancel}
              disabled={saving}
              style={cancelBtnStyle}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {sourceDocument && <RelatedDocumentReviewPanel key={JSON.stringify([documentId, rowVersion, sourceDocument.Classification, sourceDocument.ClassifiedAt, sourceDocument.ClassificationStatus, sourceDocument.ClassificationApprovedAt, sourceDocument.ClassificationApprovedValue, sourceDocument.GeneratedFromTemplateId, sectionCode, reviewOperation])}
        document={sourceDocument} canSelect={canEdit && !saving && !stale}
        {...(reviewOperation === 'new' ? {} : { operation: reviewOperation })}
        onSelect={prior => {
          if (pending === null) setExpectedRevision(rowVersion);
          setPending(pending ?? current);
          setSelectedPrior(prior);
          selectRef.current?.focus();
        }} />}
    </div>
  );
}
